import React from 'react';
import LicenseBadge from './LicenseBadge';
import { lifecycleStatus, heartbeatStatus } from './licenseStatus';

/**
 * <OnPremDeploymentList> - zeigt die ONPREM-Deployments eines Mandanten (Anzeigen)
 * und bietet je Deployment die Verwaltungs-Aktionen an (Erweitern/Verwalten).
 *
 * Props:
 *   deployments  - Array aus GET /admin/tenants/{id}/onprem-licenses -> deployments.
 *   loading      - bool.
 *   onAction     - (actionType, dep) => void; actionType in
 *                  follow-up | revoke | approve | status | reset-instance.
 *   canManage    - bool (platform_admin).
 *   t            - Uebersetzer.
 */
function Field({ label, value, mono }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text)', fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>
        {value || '-'}
      </div>
    </div>
  );
}

export default function OnPremDeploymentList({ deployments, loading, onAction, canManage, t }) {
  const [filter, setFilter] = React.useState('active');
  if (loading) {
    return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('common.loading', 'Lade...')}</div>;
  }
  if (!deployments || deployments.length === 0) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
        {t('license.onprem.list.empty', 'Noch keine ONPREM-Deployments fuer diesen Mandanten.')}
      </div>
    );
  }

  const actions = [
    { key: 'edit',           labelKey: 'license.onprem.action.edit',          fallback: 'Bearbeiten',       danger: false },
    { key: 'extend',         labelKey: 'license.onprem.action.extend',        fallback: 'Verlaengern',      danger: false },
    { key: 'follow-up',      labelKey: 'license.onprem.action.follow_up',     fallback: 'Folgelizenz deponieren', danger: false },
    { key: 'approve',        labelKey: 'license.onprem.action.approve',       fallback: 'Freigeben',        danger: false },
    { key: 'status',         labelKey: 'license.onprem.action.status',        fallback: 'Status setzen',    danger: false },
    { key: 'reset-instance', labelKey: 'license.onprem.action.reset',         fallback: 'Instanz ruecksetzen', danger: true },
    { key: 'revoke',         labelKey: 'license.onprem.action.revoke',        fallback: 'Widerrufen',       danger: true },
  ];

  const visible = (deployments || []).filter((d) => {
    if (filter === 'all') return true;
    if (filter === 'with_revoked') return d.status !== 'decommissioned';
    return !d.revoked && d.status !== 'decommissioned';
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
          {t('license.onprem.list.filter', 'Anzeigen')}
        </span>
        <select className="field-input" style={{ width: 'auto', fontSize: 12.5 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="active">{t('license.onprem.list.filter.active', 'Nur aktive')}</option>
          <option value="with_revoked">{t('license.onprem.list.filter.with_revoked', 'Aktive + widerrufene')}</option>
          <option value="all">{t('license.onprem.list.filter.all', 'Alle')}</option>
        </select>
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{visible.length}/{deployments.length}</span>
      </div>
      {visible.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
          {t('license.onprem.list.filter_empty', 'Keine Deployments fuer diesen Filter.')}
        </div>
      )}
      {visible.map((dep) => {
        const life = lifecycleStatus(dep);
        const hb = heartbeatStatus(dep);
        return (
          <div
            key={dep.deployment_id}
            style={{
              border: '1px solid var(--border)', borderRadius: 10,
              background: 'var(--bg-surface)', padding: 14,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{dep.host || t('license.onprem.list.no_host', '(kein Host)')}</span>
              <LicenseBadge tone="muted">{dep.tool_id}</LicenseBadge>
              <LicenseBadge tone="muted">{dep.environment}</LicenseBadge>
              <LicenseBadge tone={life.tone} title={life.days != null ? t('license.onprem.list.days', '{n} Tage').replace('{n}', String(life.days)) : undefined}>
                {t(`license.onprem.lifecycle.${life.key}`, life.key)}
              </LicenseBadge>
              <LicenseBadge tone={hb.tone} title={hb.days != null ? t('license.onprem.list.hb_days', 'vor {n} Tagen').replace('{n}', String(hb.days)) : undefined}>
                {t('license.onprem.list.hb', 'Heartbeat')}: {t(`license.onprem.heartbeat.${hb.key}`, hb.key)}
              </LicenseBadge>
            </div>

            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
              <Field label={t('license.onprem.list.binding', 'Bindung')} value={dep.binding} />
              <Field label={t('license.onprem.list.valid_until', 'Gueltig bis')} value={dep.valid_until} />
              <Field label={t('license.onprem.list.grace', 'Kulanz (Tage)')} value={String(dep.grace_days ?? '-')} />
              <Field label={t('license.onprem.list.last_seen', 'Zuletzt gesehen')} value={dep.last_seen ? new Date(dep.last_seen).toLocaleString() : '-'} />
              <Field label="Instance-ID" value={dep.instance_id} mono />
              <Field label="JTI" value={dep.jti} mono />
              <Field label={t('license.onprem.list.owner', 'Kontakt / Owner')} value={dep.owner_contact} />
              <Field label={t('license.onprem.list.notes', 'Notizen')} value={dep.notes} />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {actions.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={a.danger ? 'btn btn-ghost btn-sm' : 'btn btn-ghost btn-sm'}
                  onClick={() => onAction(a.key, dep)}
                  disabled={!canManage}
                  style={a.danger ? { color: 'var(--danger, #ef4444)' } : undefined}
                >
                  {t(a.labelKey, a.fallback)}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
